import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedLocation } from 'hooks/useTypedLocation';
import { PSD2SessionStatusResponse } from 'hooks/webapi/psd/usePsd2Offline';

type PSD2ErrorText = {
  title: string;
  description: string;
};

export const is352Error = (
  error:
    | Pick<
        PSD2SessionStatusResponse,
        'typeOfService' | 'status' | 'errorReason' | 'transactionHistory'
      >
    | undefined,
): boolean => {
  if (!error) return false;
  const { typeOfService, errorReason } = error;

  if (typeOfService === 'PDF_PARSER' && errorReason?.includes('352'))
    return true;

  return false;
};

export const usePSD2ErrorText = (): PSD2ErrorText => {
  const location = useTypedLocation<'PSD2Error'>();
  const { t } = useTranslation();

  const errorText = useMemo((): PSD2ErrorText => {
    const error = location.state?.error;

    if (!error) {
      return {
        title: t('psd2_error:title'),
        description: t('psd2_error:description'),
      };
    }

    const { typeOfService, transactionHistory, errorReason } = error;

    // 352 Error
    if (is352Error(error)) {
      return {
        title: t('psd2_error:title'),
        description: t('psd2_error:description_unsupported_format'),
      };
    }

    // PDF_PARSER specific error handling
    if (typeOfService === 'PDF_PARSER') {
      // Check for specific error reasons
      if (
        errorReason?.includes('OLD') ||
        errorReason?.includes('DOESNT HAVE CONSECUTIVE INTERVALS') ||
        transactionHistory === 'OUT_OF_RANGE'
      ) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_old_statement'),
        };
      }

      if (errorReason?.includes('60')) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_password_protected'),
        };
      }

      if (
        transactionHistory === 'WRONG_STATEMENT_AMOUNT' ||
        errorReason?.includes('Error converting value') ||
        errorReason?.includes('ERR_OCR_LOW_RESOLUTION') ||
        errorReason?.includes('Bank timeout error')
      ) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_processing_error'),
        };
      }

      if (transactionHistory === 'NAME_SURNAME_MISMATCH') {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_name_mismatch'),
        };
      }

      if (errorReason?.includes('NO_INCOME_FOUND')) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_no_income'),
        };
      }
    }

    // TRANSACTION_HISTORY and ALL typeOfService error handling
    if (typeOfService === 'TRANSACTION_HISTORY' || typeOfService === 'ALL') {
      // Check for InvalidAccountType error
      if (errorReason?.includes('InvalidAccountType')) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_invalid_account_type'),
        };
      }

      // Check for NAME_SURNAME_MISMATCH transaction history
      if (transactionHistory === 'NAME_SURNAME_MISMATCH') {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_account_name_mismatch'),
        };
      }

      // Check for NO_INCOME_FOUND error
      if (errorReason?.includes('NO_INCOME_FOUND')) {
        return {
          title: t('psd2_error:title'),
          description: t('psd2_error:description_account_no_income'),
        };
      }
    }

    // Default fallback
    return {
      title: t('psd2_error:title'),
      description: t('psd2_error:description'),
    };
  }, [location.state?.error]);

  return errorText;
};
